import { useCallback, useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import './VirtualTableCoreStyles.scss';
import './VirtualTable.scss';

import { isDefined } from '@utils/isDefined';

import { useFilters } from '../hooks/useFilters';
import { useUrlTableState } from '../hooks/useUrlTableState';
import { useCheckboxes } from '../Checkbox/hooks/useCheckboxes';
import { MadeColumnType, useMakeColumns } from '../hooks/useMakeColumns';
import { useChangeTable } from '../hooks/useChangeTable';
import { TableControls } from '../TableControls';
import { Title } from '../Title';
import { SelectedRowsMainActionButtons } from '../SelectedRowsMainActionButtons';
import { Preloader } from '@components/Preloader';
import { PreloaderModifiers, PreloaderTheme } from '@components/Preloader/Preloader';
import { TableHeader } from './TableHeader';
import { TablePanel } from './TablePanel';
import { TableBody } from './TableBody';
import { TableAction } from './types';
import type { VirtualTableProps } from './types';
import { CHECKBOX_COLUMN_WIDTH, EXPAND_COLUMN_WIDTH } from './constants';
import { useVirtualScroll } from './useVirtualScroll';
import { useLocalSort } from './useLocalSort';
import { useCalculatedHeight } from './useCalculatedHeight';

function getRecordKey(record: any, rowKey: string | ((record: any) => string)): string {
    return typeof rowKey === 'function' ? rowKey(record) : record[rowKey];
}

export const VirtualTable = observer(
    ({
        withoutBorderRadius = false,
        tableTitle,
        dataSource = [],
        onLoadMore,
        withCheckboxes,
        count = dataSource.length,
        height,
        withoutCount,
        minHeight,
        presetFiltersParams,
        onFiltersChange,
        searchParams,
        selectedRowsActions,
        mainButtonParams,
        selectedFilters,
        actions,
        rowKey,
        urlKey = 'table',
        emptyText,
        onRow,
        titleModifiers = [],
        additionalButtonsParams,
        rowMainActionParams,
        onSortingOrderChange,
        onChange,
        lastViewedItems,
        rowClassName,
        smallButtonParams,
        columns: tableColumns,
        rowSelection: rowSelectionParent,
        rowForceUnselectParams,
        loading: loadingProp,
        footer,
        expandable,
        disableMinHeight,
        withSelectedCount = true,
    }: VirtualTableProps) => {
        const tableRef = useRef<HTMLDivElement>(null);

        const { columns } = useMakeColumns({ tableColumns });
        const { localSort, setLocalSort, sortedDataSource } = useLocalSort({ dataSource, columns });

        const calculatedHeight = useCalculatedHeight(tableRef);

        const bodyMaxHeight = height ?? calculatedHeight;
        const bodyMinHeight = disableMinHeight ? undefined : (minHeight ?? calculatedHeight);

        const getRecordKeyMemo = useCallback((record: any) => getRecordKey(record, rowKey), [rowKey]);

        const dataSourceWithKey = useMemo(
            () =>
                sortedDataSource.map(r => ({
                    ...r,
                    key: getRecordKeyMemo(r),
                })),
            [sortedDataSource, getRecordKeyMemo],
        );

        const { rowSelection } = useCheckboxes({
            dataSource: dataSourceWithKey,
            rowSelectionParent,
            rowForceUnselectParams,
        });

        const {
            searchParams: urlSearchParams,
            searchValue,
            readUrlState,
            hydrateSearchFromUrl,
            writeColumnFiltersToUrl,
            writePresetToUrl,
            writeSortToUrl,
            clearUrlFilters,
            onSearchValueChange,
        } = useUrlTableState({ urlKey, columns, searchParams });

        const { onTableChange } = useChangeTable({
            onChange,
            onFiltersChange,
            onSortingOrderChange,
            writeColumnFiltersToUrl,
            writeSortToUrl,
        });

        // Локальная сортировка встаёт сразу, серверная уходит тем же путём, что и клик по колонке.
        const hydrateSortFromUrl = (): Promise<void> | void => {
            const { sort } = readUrlState();

            if (!sort) {
                return;
            }

            setLocalSort(sort);

            return onTableChange(
                {},
                {},
                { column: { key: sort.columnKey }, order: sort.order },
                { action: TableAction.Sort, currentDataSource: [] },
            );
        };

        const { hasPresetFilters, hasFilters, selectedPreset, onResetFilters, onPresetFilterClick, buildAllFilters } =
            useFilters({
                selectedFilters,
                presetFiltersParams,
                onFiltersChange,
                readUrlState,
                hydrateSearchFromUrl,
                hydrateSortFromUrl,
                writePresetToUrl,
                clearUrlFilters,
                applyPresetSort: setLocalSort,
            });

        const {
            loading,
            scrollRef,
            headerScrollRef,
            syncHeaderScroll,
            resetScroll,
            virtualItems,
            totalSize,
            measureElement,
        } = useVirtualScroll({
            dataSource: sortedDataSource,
            onLoadMore,
            getRecordKey: getRecordKeyMemo,
            lastViewedItems,
        });

        const displayColumns = useMemo(() => {
            const expandColumn: MadeColumnType[] = expandable
                ? [{ key: 'virtual-table-expand', width: expandable.columnWidth ?? EXPAND_COLUMN_WIDTH, title: '' }]
                : [];

            return (
                withCheckboxes && rowSelection
                    ? [
                          ...expandColumn,
                          {
                              key: 'virtual-table-checkbox',
                              width: CHECKBOX_COLUMN_WIDTH,
                              title: rowSelection.columnTitle,
                          },
                          ...columns,
                      ]
                    : [...expandColumn, ...columns]
            ).filter((col: { width?: number | string }) => {
                const w = col.width;
                return w !== 0 && Number(w) !== 0;
            });
        }, [withCheckboxes, rowSelection, columns, expandable]);

        return (
            <div
                className={classNames([
                    'virtual_table_core_root_container',
                    'virtual_table_root_container',
                    withoutBorderRadius && 'virtual_table_core_root_container__withoutBorderRaduis',
                    isDefined(bodyMinHeight) && 'virtual_table_core_root_container__withMinHeight',
                ])}
                style={{ '--virtual-table-core--min-height': `${bodyMinHeight}px` }}
            >
                {isDefined(tableTitle) && (
                    <div className="virtual_table_core_header">
                        <Title
                            title={tableTitle}
                            modifiers={titleModifiers}
                        />
                    </div>
                )}
                <TableControls
                    searchParams={urlSearchParams}
                    searchValue={searchValue}
                    onSearchValueChange={onSearchValueChange}
                    additionalButtonsParams={additionalButtonsParams}
                    mainButtonParams={mainButtonParams}
                    actions={actions}
                />
                <TablePanel
                    hasPresetFilters={hasPresetFilters}
                    presetFiltersParams={presetFiltersParams}
                    selectedFilters={selectedFilters}
                    selectedPreset={selectedPreset}
                    onPresetFilterClick={onPresetFilterClick}
                    count={count}
                    withoutCount={withoutCount}
                    hasFilters={hasFilters}
                    onResetFilters={onResetFilters}
                    smallButtonParams={smallButtonParams}
                    rowSelection={rowSelection}
                    selectedRowsActions={selectedRowsActions}
                    withSelectedCount={withSelectedCount && !!(withCheckboxes || rowSelectionParent)}
                />
                <div
                    className="virtual_table_core_component_root virtual_table_component_root"
                    ref={tableRef}
                >
                    <TableHeader
                        headerScrollRef={headerScrollRef}
                        displayColumns={displayColumns}
                        checkboxColumnTitle={rowSelection?.columnTitle}
                        buildAllFilters={buildAllFilters}
                        tableRef={tableRef}
                        onTableChange={onTableChange}
                        resetScroll={resetScroll}
                        dataSource={sortedDataSource}
                        localSort={localSort}
                        onSortChange={setLocalSort}
                    />
                    <TableBody
                        dataSource={sortedDataSource}
                        loading={loading}
                        emptyText={emptyText}
                        scrollRef={scrollRef}
                        maxHeight={bodyMaxHeight}
                        syncHeaderScroll={syncHeaderScroll}
                        totalSize={totalSize}
                        virtualItems={virtualItems}
                        getRecordKey={getRecordKeyMemo}
                        measureElement={measureElement}
                        displayColumns={displayColumns}
                        onRow={onRow}
                        rowClassName={rowClassName}
                        rowSelection={rowSelection}
                        expandable={expandable}
                    />
                    {footer ? <div className={'virtual_table_footer'}>{footer(sortedDataSource)}</div> : null}
                    {loading || loadingProp ? (
                        <div className="virtual_table_loading">
                            <Preloader
                                modifiers={[PreloaderModifiers.Tiny]}
                                theme={PreloaderTheme.Blue}
                            />
                        </div>
                    ) : null}
                </div>
                {rowMainActionParams && (
                    <div className="virtual_table_core_footer">
                        <SelectedRowsMainActionButtons
                            actions={rowMainActionParams}
                            selectedRowKeys={rowSelection?.selectedRowKeys ?? []}
                        />
                    </div>
                )}
            </div>
        );
    },
);
